import React from 'react';

interface SkeletonLoaderProps {
  variant?: 'card' | 'list' | 'carousel' | 'text' | 'image';
  count?: number;
  className?: string;
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({ 
  variant = 'card', 
  count = 1, 
  className = '' 
}) => {
  const renderSkeleton = () => {
    switch (variant) {
      case 'card':
        return (
          <div className={`bg-white rounded-lg shadow-sm overflow-hidden animate-pulse ${className}`}>
            <div className="h-40 sm:h-48 bg-gray-300"></div>
            <div className="p-4 sm:p-6">
              <div className="h-4 bg-gray-300 rounded w-1/4 mb-3"></div>
              <div className="h-6 bg-gray-300 rounded w-3/4 mb-3"></div>
              <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-2/3"></div>
            </div>
          </div>
        );

      case 'list':
        return (
          <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 animate-pulse ${className}`}>
            <div className="flex items-start space-x-3 sm:space-x-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-300 rounded-lg flex-shrink-0"></div>
              <div className="flex-1 min-w-0">
                <div className="h-5 bg-gray-300 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-300 rounded w-1/2 mb-3"></div>
                <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-300 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        );

      case 'carousel':
        return (
          <div className={`w-full h-64 sm:h-80 md:h-[500px] lg:h-[600px] bg-gray-300 animate-pulse flex items-center justify-center ${className}`}>
            <div className="text-center">
              <div className="w-8 h-8 bg-gray-400 rounded-full mb-4 mx-auto"></div>
              <div className="h-4 bg-gray-400 rounded w-48 mb-2 mx-auto"></div>
              <div className="h-3 bg-gray-400 rounded w-32 mx-auto"></div>
            </div>
          </div>
        );

      case 'text':
        return (
          <div className={`animate-pulse ${className}`}>
            <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
            <div className="h-4 bg-gray-300 rounded w-5/6 mb-2"></div>
            <div className="h-4 bg-gray-300 rounded w-4/6"></div>
          </div>
        );

      case 'image':
        return (
          <div className={`bg-gray-300 animate-pulse ${className}`}>
            <div className="flex items-center justify-center h-full">
              <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        );

      default:
        return (
          <div className={`h-4 bg-gray-300 rounded animate-pulse ${className}`}></div>
        );
    }
  };

  if (count === 1) {
    return renderSkeleton();
  }

  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <div key={`skeleton-${index}`}>
          {renderSkeleton()}
        </div>
      ))}
    </>
  );
};

export default SkeletonLoader;